// store/modules/manageUsers.js
import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const usersPagination = createPaginationModule('USERS');
import LoggerService from "@/services/LoggerService";

export default {
  state: {
    manageUsers: [],
    pagination: defaultPagination(),
    location: null,

    // User details
    userDetails: null,
    saveUserResponse: null,

    // Activities
    activities: null,
    adminWorkgroups: null,
    adminRoleOptions: null,

    // Save responses
    saveAdminActivitiesResponse: null,
    saveAdminWorkgroupsResponse: null,
    saveAdminRolesResponse: null,
  },

  mutations: {
    ...usersPagination.mutations,
    setManageUsers(state, payload) {
      state.manageUsers = payload;
    },

    setLocation(state, payload) {
      state.location = payload;
    },

    // For getuserdetails
    setUserDetails(state, payload) {
      state.userDetails = payload;
    },

    // For savepersondetail response
    setSaveUserResponse(state, payload) {
      state.saveUserResponse = payload;
    },

    // For activities
    setActivities(state, payload) {
      state.activities = payload;
    },

    // For admin workgroups
    setAdminWorkgroups(state, payload) {
      state.adminWorkgroups = payload;
    },
    setAdminRoleOptions(state, payload) {
      state.adminRoleOptions = payload;
    },

    // For saveadminactivities
    setSaveAdminActivitiesResponse(state, payload) {
      state.saveAdminActivitiesResponse = payload;
    },

    // For saveadminworkgroups
    setSaveAdminWorkgroupsResponse(state, payload) {
      state.saveAdminWorkgroupsResponse = payload;
    },

    setSaveAdminRolesResponse(state, payload) {
      state.saveAdminRolesResponse = payload;
    },
  },

  actions: {
    // ✅ Paginated fetch for Manage Users
    async fetchManageUsers(
      { commit },
      { locationId, search = "", page = 1, pageSize = 10, isAdmin = "", isVolunteer = "" }
    ) {
      try {
        let url = `/manage-user?locationid=${locationId}&cmd=displaypersons&page_number=${page}&page_size=${pageSize}&search=${encodeURIComponent(search)}`;
        if (isAdmin) url += `&is_admin=${encodeURIComponent(isAdmin)}`;
        if (isVolunteer) url += `&is_volunteer=${encodeURIComponent(isVolunteer)}`;
        const response = await api.get(url);

        const data = response.data.data;
        if (!data) throw new Error("Invalid API response");

        const { users, pagination, location } = data;

        // ✅ Map API user data to frontend model
        const mappedUsers = users.map((u) => ({
          id: u.id,
          firstName: u.firstname,
          lastName: u.lastname,
          email: u.email,
          mobilePhone: u.mobile_phone || "",
          smsEnabled: u.sms_enabled ?? false,
          volunteer: u.volunteer_status,
          schedulingPreferences: u.scheduling_preferences || "N/A",
          admin: u.admin_status,
          lastLogon: u.last_logon,
          scheduleInfo: u.last_volunteer_date || "N/A",
          assignmentcount: u.assignmentcount || 0,
          assignments: u.assignments || "",
        }));

        commit("setManageUsers", mappedUsers);
        commit(usersPagination.SET_PAGINATION,
          {
            currentPage: pagination.current_page,
            totalPages: pagination.total_pages,
            totalRecords: pagination.total_records,
            pageSize: pagination.page_size,
            hasPrevious: pagination.has_previous,
            hasNext: pagination.has_next,
          });
        commit("setLocation", location);
      } catch (error) {
        handleApiError(error, "Failed to fetch manage users");
        throw error;
      }
    },

    // getuserdetails
    async fetchUserDetails({ commit }, { locationId, personId }) {
      try {
        const response = await api.post(`/managelocationajax`, {
          cmd: "getuserdetails",
          locationid: locationId,
          personid: personId,
        });

        commit("setUserDetails", response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to fetch user details");
        throw error;
      }
    },

    // savepersondetail
    async updateUserDetails({ commit, rootGetters }, payload) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "savepersondetail",
          ...payload,
        });

        commit("setSaveUserResponse", response.data);
        
        // Log successful user update
        const currentUser = rootGetters.stSessionData;
        await LoggerService.logDataOperation(
          'UPDATE',
          'user',
          payload.personid,
          {
            updatedFields: Object.keys(payload).filter(key => key !== 'cmd' && key !== 'personid'),
            locationId: payload.locationid || currentUser?.locationid
          },
          currentUser?.personid
        );
        
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to update user details");
        
        // Log failed update attempt
        const currentUser = rootGetters.stSessionData;
        await LoggerService.logDataOperation(
          'UPDATE_FAILED',
          'user',
          payload.personid,
          { error: error.message },
          currentUser?.personid
        );
        
        throw error;
      }
    },

    // Admin Group APIs
    async fetchActivities({ commit }, { locationId, personId }) {
      try {
        const response = await api.post(`/managelocationajax`, {
          cmd: "getactivities",
          locationid: locationId,
          personid: personId,
        });

        commit("setActivities", response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to fetch activities");
        throw error;
      }
    },

    async fetchAdminWorkgroups({ commit }, { locationId, personId }) {
      try {
        const response = await api.get(
          `/activities/location/${locationId}/workgroups-for-admin?person_id=${personId}`
        );

        commit("setAdminWorkgroups", response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to fetch admin workgroups");
        throw error;
      }
    },

    async fetchAdminRoleOptions({ commit }, { locationId, personId }) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "getadminroleoptions",
          locationid: locationId,
          personid: personId,
        });

        const options = response.data?.output || {};
        commit("setAdminRoleOptions", options);
        return options;
      } catch (error) {
        handleApiError(error, "Failed to fetch admin role options");
        throw error;
      }
    },

    async saveAdminRoles(
      { commit, rootGetters },
      { personId, locationId, organizationAdmin, locationAdmin }
    ) {
      try {
        const payload = {
          cmd: "saveadminroles",
          personid: personId,
          locationid: locationId,
        };
        if (organizationAdmin !== undefined) payload.organization_admin = organizationAdmin;
        if (locationAdmin !== undefined) payload.location_admin = locationAdmin;

        const response = await api.post(`/manageuserajax`, payload);

        commit("setSaveAdminRolesResponse", response.data);

        const currentUser = rootGetters.stSessionData;
        await LoggerService.logDataOperation(
          'UPDATE',
          'admin_roles',
          personId,
          {
            organizationAdmin,
            locationAdmin,
            locationId,
          },
          currentUser?.personid
        );

        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to save admin roles");
        throw error;
      }
    },
    async saveAdminActivities({ commit, rootGetters }, { personId, locationId, activitiesArray }) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "saveadminactivities",
          personid: personId,
          locationid: locationId,
          activitiesarray: activitiesArray,
        });

        commit("setSaveAdminActivitiesResponse", response.data);
        
        // Log admin activities update
        const currentUser = rootGetters.stSessionData;
        await LoggerService.logDataOperation(
          'UPDATE',
          'admin_activities',
          personId,
          {
            activitiesCount: activitiesArray?.length || 0,
            locationId
          },
          currentUser?.personid
        );
        
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to save admin activities");
        throw error;
      }
    },

    async saveAdminWorkgroups(
      { commit, rootGetters },
      { personId, locationId, workgroupsArray }
    ) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "saveadminworkgroups",
          personid: personId,
          locationid: locationId,
          workgroupsarray: workgroupsArray,
        });

        commit("setSaveAdminWorkgroupsResponse", response.data);
        
        // Log admin workgroups update
        const currentUser = rootGetters.stSessionData;
        await LoggerService.logDataOperation(
          'UPDATE',
          'admin_workgroups',
          personId,
          {
            workgroupsCount: workgroupsArray?.length || 0,
            locationId
          },
          currentUser?.personid
        );
        
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to save admin workgroups");
        throw error;
      }
    },

    async fetchAccountDeletionPreview(context, { personId, locationId }) {
      try {
        const params = {
          locationid: Number(locationId),
        };
        if (personId !== undefined && personId !== null) {
          params.personid = Number(personId);
        }
        const response = await api.get("/account-deletion/preview", {
          params,
        });

        const result = response.data;
        if (
          result?.success !== true
          || !Array.isArray(result.data?.locations)
          || !Array.isArray(result.data?.assignments)
        ) {
          throw new Error(result?.message || "Invalid account deletion preview response");
        }

        return result.data;
      } catch (error) {
        handleApiError(error, "Failed to fetch account deletion preview");
        throw error;
      }
    },

    async submitAccountDeletion(context, { personId, locationId, scope, resolution, message, resetAutoSchedule }) {
      const payload = {
        locationid: Number(locationId),
        scope,
        resolution,
      };
      if (personId !== undefined && personId !== null) {
        payload.personid = Number(personId);
      }
      if (typeof message === "string" && message.trim()) {
        payload.message = message.trim();
      }
      if (typeof resetAutoSchedule === "boolean") {
        payload.reset_auto_schedule = resetAutoSchedule;
      }

      try {
        const response = await api.post("/account-deletion", payload);
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to apply account action");
        throw error;
      }
    },

    async fetchFutureAssignments(context, { personId, locationId }) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "getfutureassignments",
          personid: personId,
          locationid: locationId,
        });

        const assignments = response.data?.output;
        if (!Array.isArray(assignments)) {
          throw new Error("Invalid future assignments response");
        }
        return assignments;
      } catch (error) {
        handleApiError(error, "Failed to fetch future assignments");
        throw error;
      }
    },

    // deleteperson
    async deleteUser({ rootGetters }, { personId, locationId, rerunAiWorkgroupIds = [] }) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "deleteperson",
          personid: personId,
          locationid: locationId,
          rerun_ai_workgroup_ids: rerunAiWorkgroupIds,
        });

        // Log user deletion
        const currentUser = rootGetters.stSessionData;
        await LoggerService.logDataOperation(
          'DELETE',
          'user',
          personId,
          { locationId },
          currentUser?.personid
        );

        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to delete user");
        
        // Log failed deletion attempt
        const currentUser = rootGetters.stSessionData;
        await LoggerService.logDataOperation(
          'DELETE_FAILED',
          'user',
          personId,
          { error: error.message, locationId },
          currentUser?.personid
        );
        
        throw error;
      }
    },
  },

  getters: {
    allManageUsers: (state) => state.manageUsers,
    manageUsersPagination: (state) => state.pagination,
    manageUsersLocation: (state) => state.location,

    // User detail
    userDetails: (state) => state.userDetails,
    saveUserResponse: (state) => state.saveUserResponse,

    // Activities
    activities: (state) => state.activities,
    adminWorkgroups: (state) => state.adminWorkgroups,
    adminRoleOptions: (state) => state.adminRoleOptions,

    // Save responses
    saveAdminActivitiesResponse: (state) => state.saveAdminActivitiesResponse,
    saveAdminWorkgroupsResponse: (state) => state.saveAdminWorkgroupsResponse,
    saveAdminRolesResponse: (state) => state.saveAdminRolesResponse,
  },
};
